/**
 * Time-value-of-money maths for the Portfolio Planner.
 *
 * Ported from the reference prototype's `js/tvm.js`
 * (MF Portfolio Calculator/SMEST-Portfolio-Planner) so the live badges the
 * planner form paints while the user types agree, to the rupee, with the plan
 * the backend later generates. The rulebook requires recalculation on every
 * keystroke, so these must never become network calls.
 *
 * Conventions locked to the rulebook's worked examples:
 *   • lumpsum / inflation — annual compounding, period in years (may be fractional)
 *   • SIP — compounding at the SIP frequency, END mode (ordinary annuity)
 *
 * Verified against the rulebook: ₹25,00,000 × 1.08^6 = ₹39,67,186;
 * ₹10,00,000 × 1.11^6 = ₹18,70,415; the ₹20,96,771 gap over 72 months at 11%
 * needs a ₹20,690 monthly SIP.
 */

import type { PlannerFrequency, PlannerSegment } from "./types";

/* The engine accepts four SIP frequencies. The extra spellings are tolerated
   because bond coupon frequencies arrive on a different convention — live
   responses carry "Half Yearly" without the hyphen — and a coupon frequency
   silently falling through to monthly would misprice a sweep. */
const FREQ_PER_YEAR: Record<string, number> = {
  Monthly: 12,
  Quarterly: 4,
  "Half-Yearly": 2,
  "Half Yearly": 2,
  "Semi-Annual": 2,
  "Semi Annual": 2,
  Annual: 1,
  Annually: 1,
  Yearly: 1,
  Weekly: 52,
  Daily: 365,
};

export const freqPerYear = (freq?: string): number =>
  FREQ_PER_YEAR[freq ?? "Monthly"] ?? 12;

/** Future value of a one-time amount, annual compounding. */
export const fvLumpsum = (
  presentValue: number,
  annualRatePct: number,
  years: number
): number => presentValue * Math.pow(1 + annualRatePct / 100, years);

/** A goal's cost inflated to its goal date — the real target. */
export const inflate = (
  costToday: number,
  inflationPct: number,
  years: number
): number => costToday * Math.pow(1 + inflationPct / 100, years);

/** Future value of a level SIP (ordinary annuity, END mode). */
export const fvSIP = (
  amountPerPeriod: number,
  annualRatePct: number,
  totalMonths: number,
  freq: PlannerFrequency = "Monthly"
): number => {
  const perYear = freqPerYear(freq);
  const periods = Math.round((totalMonths / 12) * perYear);
  const rate = annualRatePct / 100 / perYear;
  if (periods <= 0) return 0;
  if (rate === 0) return amountPerPeriod * periods;
  return amountPerPeriod * ((Math.pow(1 + rate, periods) - 1) / rate);
};

/** Instalment per period required to reach a target FV, END mode. */
export const sipForTarget = (
  targetFV: number,
  annualRatePct: number,
  totalMonths: number,
  freq: PlannerFrequency = "Monthly"
): number => {
  const perYear = freqPerYear(freq);
  const periods = Math.round((totalMonths / 12) * perYear);
  const rate = annualRatePct / 100 / perYear;
  if (periods <= 0) return targetFV;
  if (rate === 0) return targetFV / periods;
  return targetFV / ((Math.pow(1 + rate, periods) - 1) / rate);
};

/** Number of level instalments over a span — for schedule building. */
export const instalments = (
  totalMonths: number,
  freq: PlannerFrequency = "Monthly"
): number => Math.max(0, Math.round((totalMonths / 12) * freqPerYear(freq)));

/** A SIP at any frequency expressed as its monthly equivalent. */
export const monthlyEquivalent = (
  amountPerPeriod: number,
  freq: PlannerFrequency = "Monthly"
): number =>
  freq === "Monthly"
    ? amountPerPeriod
    : (amountPerPeriod * freqPerYear(freq)) / 12;

/* ────────────────────────── loans / liabilities ─────────────────────── */

/** EMI from principal, annual rate and tenure in months. */
export const emiFromRate = (
  principal: number,
  annualRatePct: number,
  months: number
): number => {
  const rate = annualRatePct / 100 / 12;
  if (months <= 0) return principal;
  if (rate === 0) return principal / months;
  const factor = Math.pow(1 + rate, months);
  return (principal * rate * factor) / (factor - 1);
};

/**
 * Annual rate % implied by an EMI, by bisection — the inverse of
 * `emiFromRate`, which has no closed form. Powers the form's two-way
 * EMI ⇄ interest-rate link.
 */
export const rateFromEMI = (
  principal: number,
  emi: number,
  months: number
): number => {
  if (months <= 0 || emi <= 0 || principal <= 0) return 0;
  // Paying back no more than was borrowed implies a zero (or negative) rate.
  if (emi * months <= principal) return 0;

  let low = 0.000001;
  let high = 1.0; // monthly rate bounds: 0 … 100% per month
  const residual = (rate: number) => {
    const factor = Math.pow(1 + rate, months);
    return (principal * rate * factor) / (factor - 1) - emi;
  };

  let residualLow = residual(low);
  for (let step = 0; step < 200; step += 1) {
    const mid = (low + high) / 2;
    const residualMid = residual(mid);
    if (Math.abs(residualMid) < 1e-7) return mid * 12 * 100;
    if (residualLow < 0 === residualMid < 0) {
      low = mid;
      residualLow = residualMid;
    } else {
      high = mid;
    }
  }
  return ((low + high) / 2) * 12 * 100;
};

/** Total interest payable over the life of a loan. */
export const totalInterest = (
  principal: number,
  emi: number,
  months: number
): number => Math.max(0, emi * months - principal);

/* ──────────────────────────── dates & tenure ────────────────────────── */

export const monthsBetween = (from: Date, to: Date): number => {
  const years = to.getFullYear() - from.getFullYear();
  const months = to.getMonth() - from.getMonth();
  const days = to.getDate() - from.getDate();
  return years * 12 + months + days / 30.4375;
};

/** Calendar-safe month arithmetic, clamped to the end of short months. */
export const addMonths = (date: Date, count: number): Date => {
  const result = new Date(date.getTime());
  const targetDay = result.getDate();
  result.setMonth(result.getMonth() + count);
  if (result.getDate() < targetDay) result.setDate(0);
  return result;
};

export const toISODate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

/**
 * Thumb rule, mirroring the engine's `segmentForYears`. The rulebook reads
 * "0–3 / 3–6 / 6+", which is ambiguous at the endpoints; its own worked example
 * calls a 6-year goal Hybrid, so both boundaries are inclusive of the lower
 * bucket. Boundaries come from bootstrap `config`, never a local constant.
 */
export const segmentForYears = (
  years: number,
  boundaries: { shortMaxYears: number; midMaxYears: number }
): PlannerSegment => {
  if (years <= boundaries.shortMaxYears) return "Debt";
  if (years <= boundaries.midMaxYears) return "Hybrid";
  return "Equity";
};

export const segmentTermLabel = (segment: PlannerSegment): string =>
  segment === "Debt"
    ? "Short term (0–3 yr)"
    : segment === "Hybrid"
      ? "Medium term (3–6 yr)"
      : "Long term (6 yr +)";
